// The Summary tab (R8, screen 20): one card per question over the rows the
// table's filters admit. The numbers are aggregated on the server; this page
// lays them out and picks the picture — the design's horizontal bars for a
// list of choices, a donut for yes/no, columns for a range, a line for days,
// a heat table for a grid, the newest few answers for free text.
//
// The view and the date range live in the URL, which is the same URL the
// Submissions tab writes its filters to, so moving between the two keeps
// what you were looking at.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  type FormSummaryData, type QuestionSummary, type SubmissionView, getFormSummary, listViewsApi, submissionQueryString,
} from '../../api/forms';
import { BarChart, type CsvRow, Donut, Figure, Histogram, LineChart } from '../../shared/charts';
import { SectionLabel } from '../../components/fs';
import { useFormWorkspace } from './FormWorkspace';
/** The params the table writes that are not the view or the range. */
const TABLE_ONLY = ['q', 'delivery', 'connectionId', 'match', 'conditions', 'page', 'limit', 'sort', 'dir'];

const num = (n: number | null | undefined, places = 2) =>
  n === null || n === undefined ? '—' : Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: places });
const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : '0%');
const shortDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return Number.isFinite(m) && Number.isFinite(d) ? `${m}/${d}${y && y !== new Date().getFullYear() ? `/${String(y).slice(2)}` : ''}` : day;
};
const range = (from: number, to: number) => `${num(from)}–${num(to)}`;

/**
 * The Net Promoter Score of a 0–10 scale: promoters (9–10) minus detractors
 * (0–6), as a share of everyone who answered. Only a 0–10 scale has one.
 */
export function npsScore(r: { min: number; max: number; items: { value: number; count: number }[] }):
  { score: number; promoters: number; passives: number; detractors: number } | null {
  if (r.min !== 0 || r.max !== 10) return null;
  let promoters = 0, passives = 0, detractors = 0;
  for (const i of r.items) {
    if (i.value >= 9) promoters += i.count;
    else if (i.value >= 7) passives += i.count;
    else detractors += i.count;
  }
  const n = promoters + passives + detractors;
  if (!n) return null;
  return { score: Math.round(((promoters - detractors) / n) * 100), promoters, passives, detractors };
}

/** What one card says beside its title: "{n} answered", with the fuller sentence as its title. */
function answeredLine(q: QuestionSummary, total: number): { short: string; full: string } {
  if (q.instances !== undefined) {
    const label = (q.scopeLabel || 'entry').toLowerCase();
    return {
      short: `${num(q.answered)} answered`,
      full: `${num(q.answered)} of ${num(q.instances)} ${label}${q.instances === 1 ? '' : 's'} across ${num(total)} submission${total === 1 ? '' : 's'}`,
    };
  }
  return { short: `${num(q.answered)} answered`, full: `${num(q.answered)} of ${num(total)} answered · ${pct(q.answered, total)}` };
}

/** The page's sentence: "7 responses · last 30 days", "· since 2026-09-01", "· all time". */
export function rangeWords(from: string, to: string): string {
  if (from && to) return `${from} → ${to}`;
  if (from) return `since ${from}`;
  if (to) return `until ${to}`;
  return 'all time';
}

function QuestionCard({ q, total }: { q: QuestionSummary; total: number }) {
  const r = q.result;
  const title = q.label;
  let body: React.ReactNode;

  if (r.kind === 'choice') {
    const items: { label: string; value: number; other?: boolean }[] = [
      ...r.items.map((i) => ({ label: i.label, value: i.count })),
      ...(r.other > 0 ? [{ label: 'Other', value: r.other, other: true }] : []),
    ];
    const sum = items.reduce((n, i) => n + i.value, 0);
    const rows: CsvRow[] = items.map((i) => [i.label, i.value, r.multi ? pct(i.value, q.answered) : pct(i.value, sum)]);
    const header = ['Choice', 'Count', r.multi ? 'Of respondents' : 'Share'];
    // The design's card (screen 20): one bar per choice with "count · %".
    body = (
      <Figure title={`${title} — choices`} header={header} rows={rows}>
        <BarChart title={title} total={r.multi ? q.answered : sum} data={items.map((i) => ({ label: i.label, value: i.value, muted: !!i.other }))} />
        {r.multi && <div className="fs-cell-sub" style={{ marginTop: 6 }}>Respondents could pick more than one, so the bars need not add up.</div>}
      </Figure>
    );
  } else if (r.kind === 'rating') {
    const rows: CsvRow[] = r.items.map((i) => [String(i.value), i.count]);
    body = (
      <>
        <div className="fsum-stat">
          <span className="fsum-big">{r.mean === null ? '—' : num(r.mean, 1)}</span>
          <span className="fs-cell-sub">average of {r.max}</span>
        </div>
        <Figure title={`${title} — ratings`} header={['Rating', 'Count']} rows={rows}>
          <Histogram title={title} data={r.items.map((i) => ({ label: String(i.value), value: i.count }))} />
        </Figure>
      </>
    );
  } else if (r.kind === 'number') {
    const rows: CsvRow[] = r.buckets.map((b) => [range(b.from, b.to), b.count]);
    body = (
      <>
        <div className="fsum-stat fsum-facts">
          <span><b>{num(r.count)}</b> numbers</span>
          <span>min <b>{num(r.min)}</b></span>
          <span>max <b>{num(r.max)}</b></span>
          <span>average <b>{num(r.mean)}</b></span>
        </div>
        {r.buckets.length > 0 && (
          <Figure title={`${title} — distribution`} header={['Range', 'Count']} rows={rows}>
            <Histogram title={title} data={r.buckets.map((b) => ({ label: r.buckets.length === 1 ? num(b.from) : num(b.from), value: b.count }))} />
          </Figure>
        )}
      </>
    );
  } else if (r.kind === 'date') {
    const rows: CsvRow[] = r.days.map((d) => [d.day, d.count]);
    body = r.days.length === 0
      ? <div className="fs-cell-sub">No dates yet.</div>
      : (
        <Figure title={`${title} — by day`} header={['Day', 'Count']} rows={rows}>
          <LineChart title={title} data={r.days.map((d) => ({ label: shortDay(d.day), value: d.count }))} />
        </Figure>
      );
  } else if (r.kind === 'scale') {
    const rows: CsvRow[] = r.items.map((i) => [String(i.value), i.count]);
    const nps = npsScore(r);
    body = (
      <>
        <div className="fsum-stat">
          <span className="fsum-big">{r.mean === null ? '—' : num(r.mean, 1)}</span>
          <span className="fs-cell-sub">average on {r.min}–{r.max}</span>
          {nps && (
            <>
              <span className="fsum-big fsum-nps" data-testid="nps-score">{nps.score > 0 ? `+${nps.score}` : nps.score}</span>
              <span className="fs-cell-sub" title={`${nps.promoters} promoters (9–10), ${nps.passives} passives (7–8), ${nps.detractors} detractors (0–6)`}>
                NPS · {nps.promoters} promoters, {nps.detractors} detractors
              </span>
            </>
          )}
        </div>
        <Figure title={`${title} — answers`} header={['Answer', 'Count']} rows={rows}>
          <Histogram title={title} data={r.items.map((i) => ({ label: String(i.value), value: i.count }))} />
        </Figure>
      </>
    );
  } else if (r.kind === 'matrix') {
    const header = ['Row', ...r.columns.map((c) => c.label)];
    const rows: CsvRow[] = r.rows.map((row, ri) => [row.label, ...r.counts[ri]]);
    const peak = Math.max(1, ...r.counts.flat());
    body = r.rows.length === 0 || r.columns.length === 0
      ? <div className="fs-cell-sub">This grid has no rows or columns yet.</div>
      : (
        <Figure title={`${title} — by row`} header={header} rows={rows}>
          <div style={{ overflowX: 'auto' }}>
            <table className="fs-table" style={{ width: '100%', fontSize: 13 }} aria-hidden="true">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }} />
                  {r.columns.map((c) => <th key={c.value} style={{ textAlign: 'center', fontWeight: 600, color: 'var(--ink-500)' }}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {r.rows.map((row, ri) => {
                  const rowTotal = r.counts[ri].reduce((n, c) => n + c, 0);
                  return (
                    <tr key={row.value}>
                      <th style={{ textAlign: 'left', fontWeight: 500, color: 'var(--ink-800)', padding: '6px 8px 6px 0' }}>{row.label}</th>
                      {r.counts[ri].map((n, ci) => (
                        <td key={ci} style={{
                          textAlign: 'center', padding: '6px 8px', borderRadius: 6,
                          background: n > 0 ? `color-mix(in srgb, var(--accent, #667eea) ${Math.round((n / peak) * 60)}%, transparent)` : undefined,
                          color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums',
                        }}>
                          {n}{rowTotal > 0 && !r.multi ? <span className="fs-cell-sub" style={{ marginLeft: 4 }}>{pct(n, rowTotal)}</span> : null}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {r.multi && <div className="fs-cell-sub" style={{ marginTop: 6 }}>Respondents could pick more than one per row, so a row's counts need not add up.</div>}
        </Figure>
      );
  } else if (r.kind === 'ranking') {
    const rows: CsvRow[] = r.items.map((i, n) => [`${n + 1}`, i.label, i.mean === null ? '' : num(i.mean, 2), i.first, i.count]);
    body = r.items.length === 0
      ? <div className="fs-cell-sub">Nothing to rank yet.</div>
      : (
        <Figure title={`${title} — order`} header={['Place', 'Item', 'Average position', 'Ranked first', 'Ranked']} rows={rows}>
          <div className="fs-cell-sub" style={{ marginBottom: 6 }}>Best average position first · bars show how often each was ranked first</div>
          <BarChart title={title} total={q.answered} data={r.items.map((i, n) => ({ label: `${n + 1}. ${i.label}${i.mean === null ? '' : ` · avg ${num(i.mean, 1)}`}`, value: i.first }))} />
        </Figure>
      );
  } else if (r.kind === 'yesno') {
    const rows: CsvRow[] = [['Yes', r.yes, pct(r.yes, r.yes + r.no)], ['No', r.no, pct(r.no, r.yes + r.no)]];
    body = (
      <Figure title={`${title} — yes or no`} header={['Answer', 'Count', 'Share']} rows={rows}>
        <Donut title={title} data={[{ label: 'Yes', value: r.yes }, { label: 'No', value: r.no }]} />
      </Figure>
    );
  } else {
    body = r.latest.length === 0
      ? <div className="fs-cell-sub">No answers yet.</div>
      : (
        <>
          <div className="fs-cell-sub" style={{ marginBottom: 6 }}>Latest answers</div>
          <ul className="fsum-latest">
            {r.latest.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
          <div className="fsum-more">
            <Link className="fs-btn text sm" to={`../submissions`} relative="path">See every answer in the table →</Link>
          </div>
        </>
      );
  }

  const answered = answeredLine(q, total);
  return (
    <div className="fs-card fsum-card" data-testid="summary-card">
      <div className="fsum-card-head">
        <h3>{title}</h3>
        <span className="fsum-answered" title={answered.full}>{answered.short}</span>
      </div>
      {body}
    </div>
  );
}

export default function FormSummary() {
  const { form } = useFormWorkspace();
  const [params, setParams] = useSearchParams();
  const [views, setViews] = useState<SubmissionView[]>([]);
  const [data, setData] = useState<FormSummaryData | null>(null);
  const [error, setError] = useState('');

  const viewId = params.get('view') ? Number(params.get('view')) : null;
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  // Filters the table wrote that no view here explains — shown as a note, with a way out.
  const tableFilters = viewId === null ? TABLE_ONLY.filter((k) => params.has(k) && !['page', 'limit', 'sort', 'dir'].includes(k)) : [];
  const query = params.toString();

  useEffect(() => {
    listViewsApi(form.id).then((r) => setViews(r.views)).catch(() => undefined);
  }, [form.id]);

  useEffect(() => {
    setData(null);
    setError('');
    getFormSummary(form.id, query)
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load the summary.'));
  }, [form.id, query]);

  /** Keep the view and the range; drop or set the rest. */
  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };

  /**
   * A view is written to the URL as its filters, the way the table writes
   * them, plus `view=` to mark the button — so stepping across to the table
   * shows the same rows, and the server answers the view by its id.
   */
  const openView = (v: SubmissionView | null) => {
    const p = new URLSearchParams(v ? submissionQueryString(v.config?.filters || {}) : '');
    p.delete('from');
    p.delete('to');
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (v) p.set('view', String(v.id));
    setParams(p, { replace: true });
  };

  const sections = useMemo(() => {
    const out: { title: string; questions: QuestionSummary[] }[] = [];
    for (const q of data?.questions || []) {
      const last = out[out.length - 1];
      if (last && last.title === q.section) last.questions.push(q);
      else out.push({ title: q.section, questions: [q] });
    }
    return out;
  }, [data]);

  const tableHref = `/admin/forms/${form.id}/submissions${query ? `?${query}` : ''}`;

  return (
    <div className="fsum-page">
      <div className="fsum-head">
        <h1>
          {data ? `${num(data.total)} response${data.total === 1 ? '' : 's'}` : 'Summary'} · {rangeWords(from, to)}
          {data && data.questions.length > 0 && <span className="fsum-q"> · {num(data.questions.length)} question{data.questions.length === 1 ? '' : 's'}</span>}
        </h1>
        {data?.sampled && <span className="fsum-sampled">the newest {num(data.total)} — more matched than can be counted at once</span>}
        <div className="fs-seg" data-testid="summary-views">
          <button type="button" className={viewId === null && tableFilters.length === 0 ? 'on' : ''} onClick={() => openView(null)}>All</button>
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              className={viewId === v.id ? 'on' : ''}
              title={v.shared ? 'Shared with the team' : 'Only you can see this view'}
              onClick={() => openView(v)}
            >
              {v.name}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <label className="fsum-date">
          From
          <input className="fs-input fs-w-150" type="date" value={from} max={to || undefined}
            onChange={(e) => patch({ from: e.target.value })} aria-label="Received from" />
        </label>
        <label className="fsum-date">
          To
          <input className="fs-input fs-w-150" type="date" value={to} min={from || undefined}
            onChange={(e) => patch({ to: e.target.value })} aria-label="Received to" />
        </label>
        {(from || to) && (
          <button type="button" className="fs-btn neutral sm" onClick={() => patch({ from: null, to: null })}>Any time</button>
        )}
        <Link className="fs-btn text sm" to={tableHref}>Open these in the table →</Link>
      </div>

      {tableFilters.length > 0 && (
        <div className="fsum-note">
          The table's filters apply here too.
          <button type="button" className="fs-btn neutral sm" onClick={() => openView(null)}>Clear them</button>
        </div>
      )}

      {error && <div className="ad-err">{error}</div>}
      {!error && !data && <div className="sl-quiet">Counting…</div>}

      {data && (
        <>
          {data.total === 0 && (
            <p className="fsum-empty">
              Nothing to count yet{from || to || tableFilters.length ? ' for these filters' : ''}. The summary fills in as responses arrive.
            </p>
          )}

          {data.total > 0 && data.questions.length === 0 && (
            <p className="fsum-empty">This form has no questions that can be summarised.</p>
          )}

          {data.total > 0 && sections.map((s, i) => (
            <section key={i} className="fsum-section">
              {s.title && <SectionLabel>{s.title}</SectionLabel>}
              <div className="fsum-grid">
                {s.questions.map((q) => <QuestionCard key={`${q.scope}:${q.key}`} q={q} total={data.total} />)}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
